import { CONFIG } from './config.js';
import { createRandomBodies, disposeBodies } from './bodies/createBodies.js';
import { sampleTrail } from './bodies/trails.js';
import { CameraRig } from './camera/CameraRig.js';
import { computeAccelerations } from './physics/gravity.js';
import { advanceSimulation } from './physics/integrate.js';
import { checkEscape, createEscapeState } from './physics/escape.js';
import { planetTemperature } from './physics/temperature.js';
import { eraFromTemperature } from './narrative/era.js';
import {
  createCivilizationState,
  noteTemperature,
  resetCivilizationState,
} from './narrative/civilization.js';
import { createHistory } from './narrative/history.js';
import { createQuoteRotator } from './narrative/quotes.js';
import { createSceneContext } from './visuals/scene.js';
import { addStarfield } from './visuals/starfield.js';
import { createRadialTexture } from './visuals/textures.js';
import { createBloomPipeline } from './visuals/bloom.js';
import { createHorizon } from './visuals/horizon.js';
import { createAtmosphere } from './visuals/atmosphere.js';
import { applyEraLook } from './visuals/eraLook.js';
import { createTimeBar } from './ui/timeBar.js';
import { createHud } from './ui/hud.js';
import { createInfoPanel } from './ui/infoPanel.js';
import { createFadeOverlay } from './ui/fadeOverlay.js';
import { createPovButton } from './ui/povButton.js';
import { createNarrativeUi } from './ui/narrativeUi.js';

const app = document.getElementById('app');
const { scene, camera, renderer, resize } = createSceneContext(app);

const starPointTexture = createRadialTexture('rgba(255,255,255,0.95)', 'rgba(255,255,255,0)');
const glowTexture = createRadialTexture('rgba(255,255,255,0.9)', 'rgba(255,255,255,0)');
addStarfield(scene, starPointTexture);

const bloom = createBloomPipeline(renderer, scene, camera);
const horizon = createHorizon(scene);
const atmosphere = createAtmosphere(scene);
const fade = createFadeOverlay();
const rig = new CameraRig(camera, renderer.domElement);

let bodies = createRandomBodies(scene, glowTexture);
computeAccelerations(bodies);
rig.setBodies(bodies);

let simTime = 0;
let timeScale = 1;
let accumulator = 0;
let lastWallMs = performance.now();
let reseeding = false;
const escapeState = createEscapeState();
const civState = createCivilizationState();
const history = createHistory();
const quotes = createQuoteRotator(55 + Math.random() * 30);

const hud = createHud();
const narrativeUi = createNarrativeUi();
narrativeUi.setQuote(quotes.text);
const info = createInfoPanel({
  camera,
  getBodies: () => bodies,
});

function showNotice(text) {
  hud.showNotice(text);
}

function currentEra() {
  const planet = bodies.find((b) => !b.isStar);
  if (!planet) return { key: 'stable', label: '恒纪元' };
  return eraFromTemperature(planetTemperature(planet, bodies));
}

function beatText(event) {
  if (!event) return '';
  if (event.type === 'destroy') {
    if (event.cause === 'freezing') {
      return `第 ${event.civId} 号文明在极寒中沉睡`;
    }
    return `第 ${event.civId} 号文明在三日炙烤中毁灭`;
  }
  if (event.type === 'rebirth') {
    return `第 ${event.civId} 号文明自废墟中醒来`;
  }
  return '';
}

function handleCivEvent(event) {
  if (!event) return;
  history.push(event);
  narrativeUi.setHistory(history.list());
  const text = beatText(event);
  if (text) narrativeUi.showBeat(text, event.type === 'destroy' ? 2400 : 2000);
}

async function reseedSystem(reason) {
  if (reseeding) return;
  reseeding = true;
  try {
    await fade.fadeOut(CONFIG.FADE_SEC * 1000);
    bodies = disposeBodies(bodies);
    simTime = 0;
    accumulator = 0;
    escapeState.escapeSince = null;
    escapeState.offscreenSince = null;
    resetCivilizationState(civState);
    if (reason === 'manual') {
      history.push({ type: 'reset' });
    } else {
      history.clear();
    }
    narrativeUi.setHistory(history.list());
    rig.resetOrbitDefault();
    horizon.setVisible(false);
    atmosphere.setVisible(false);
    bodies = createRandomBodies(scene, glowTexture);
    computeAccelerations(bodies);
    rig.setBodies(bodies);
    info.hide();
    await fade.fadeIn(CONFIG.FADE_SEC * 1000);
    showNotice(reason === 'manual' ? '已重新生成星系' : '系统逃逸 · 已重新生成');
    console.info('[threebody] reseed:', reason);
  } finally {
    reseeding = false;
  }
}

createTimeBar({
  initialSpeed: 1,
  onSpeed: (s) => {
    timeScale = s;
  },
  onReseed: () => {
    reseedSystem('manual');
  },
});

const povBtn = createPovButton({
  getMode: () => rig.mode,
  onEnter: () => {
    rig.enterPov();
    horizon.setVisible(true);
    atmosphere.setVisible(true);
    povBtn.refresh();
  },
  onExit: () => {
    rig.exitPov();
    povBtn.refresh();
  },
});

let lastClickMs = 0;
renderer.domElement.addEventListener('click', (e) => {
  if (rig.mode !== 'orbit') return;
  const now = performance.now();
  if (now - lastClickMs < 280) return;
  lastClickMs = now;
  window.setTimeout(() => {
    if (performance.now() - lastClickMs < 280) return;
    info.onPointerClick(e);
  }, 220);
});

renderer.domElement.addEventListener('dblclick', (e) => {
  if (rig.mode !== 'orbit') return;
  lastClickMs = performance.now();
  e.preventDefault();
  info.onPointerClick(e);
  if (info.selected) rig.focusBody(info.selected);
});

function updateCivilization() {
  const era = currentEra();
  const year = Math.floor(simTime / CONFIG.T_YEAR);
  const event = noteTemperature(civState, era.key, { year });
  handleCivEvent(event);
}

function updateVisualEffects(eraKey) {
  const pulse = 1 + 0.03 * Math.sin(simTime * 5.5);
  const surface = rig.isSurfaceMode;
  const eye = camera.position;

  for (let i = 0; i < bodies.length; i++) {
    const body = bodies[i];
    if (!body.isStar) {
      body.mesh.scale.setScalar(1);
      continue;
    }

    const base = eraKey === 'scorching' ? 0.82 : eraKey === 'freezing' ? 0.62 : 0.72;
    body.mesh.material.emissiveIntensity = base + 0.08 * Math.sin(simTime * 4.2 + i * 1.7);

    if (surface) {
      // Keep distant suns readable as discs; do not inflate close flybys (already huge).
      const dist = Math.max(body.position.distanceTo(eye), 0.2);
      const minAng = CONFIG.POV_STAR_MIN_ANG;
      const naturalAng = body.radius / dist;
      const meshBoost = naturalAng < minAng ? Math.min(minAng / naturalAng, 10) : 1;
      body.mesh.scale.setScalar(meshBoost);
      const glowAng = Math.max(naturalAng * 2.6, minAng * 2.6);
      body.glow.scale.setScalar(dist * glowAng * pulse);
      body.glow.material.opacity = 0.4 + 0.08 * Math.sin(simTime * 3.4 + i);
      // slightly brighter cores on surface so they punch through sky
      body.mesh.material.emissiveIntensity =
        (eraKey === 'scorching' ? 1.0 : eraKey === 'freezing' ? 0.78 : 0.9) +
        0.1 * Math.sin(simTime * 4.2 + i * 1.7);
    } else {
      body.mesh.scale.setScalar(1);
      body.glow.scale.setScalar(body.radius * 2.8 * pulse);
      body.glow.material.opacity = 0.28 + 0.06 * Math.sin(simTime * 3.4 + i);
    }

    if (body.light) {
      body.light.intensity = 0.7 + body.mass * 0.45 + 0.08 * Math.sin(simTime * 3 + i);
    }
  }
}

function updateHud() {
  const year = Math.floor(simTime / CONFIG.T_YEAR);
  const era = currentEra();
  hud.update({
    year,
    cycleCount: civState.cycleCount,
    civId: civState.civId,
    eraLabel: era.label,
  });
}

function syncSurfaceKit() {
  const mode = rig.mode;
  if (mode === 'orbit') {
    horizon.setVisible(false);
    atmosphere.setVisible(false);
  } else {
    horizon.setVisible(true);
    atmosphere.setVisible(true);
  }

  const planet = bodies.find((b) => !b.isStar);
  if (planet) {
    const up = rig.getSurfaceUp();
    horizon.update(planet, up);
    atmosphere.update(planet, 32);
  }
  narrativeUi.setPovMode(mode === 'pov');
  povBtn.refresh();
}

function animate() {
  requestAnimationFrame(animate);

  const now = performance.now();
  let frameDelta = (now - lastWallMs) / 1000;
  lastWallMs = now;
  if (frameDelta > 0.05) frameDelta = 0.05;

  if (!reseeding) {
    const result = advanceSimulation(
      bodies,
      accumulator,
      timeScale,
      frameDelta,
      {
        afterBody: (body, dt) => sampleTrail(body, dt),
      },
    );
    accumulator = result.accumulator;
    simTime += result.simDt;
    if (result.steps > 0) updateCivilization();

    checkEscape({
      bodies,
      camera,
      nowMs: now,
      state: escapeState,
      onReseed: (reason) => {
        reseedSystem(reason);
      },
      onNotice: showNotice,
      // only observer orbit uses frustum offscreen reseed
      checkOffscreen: rig.shouldCheckOffscreen,
    });
  }

  const era = currentEra();
  updateHud();
  if (info.selected) info.refresh();
  updateVisualEffects(era.key);
  applyEraLook({ scene, bloom, atmosphere, horizon, eraKey: era.key });

  // wall-clock quote rotation (not sim-scaled)
  if (quotes.update(frameDelta)) narrativeUi.setQuote(quotes.text);
  narrativeUi.tick();

  rig.update(frameDelta);
  syncSurfaceKit();

  bloom.render();
}

window.addEventListener('resize', () => {
  resize();
  bloom.setSize(window.innerWidth, window.innerHeight);
});

narrativeUi.setHistory(history.list());
updateHud();
animate();
console.info('[threebody] cinematic Path C running');
